import { Layout, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import PriceFootnote from '~/app/compositions/content/calculation-info/components/price-footnote';
import {
	CalculationInfoStyle,
	PriceBarTotalPrice,
	TotalPrice,
} from '~/app/compositions/content/calculation-info/styles/calculation-info-style';
import { useAppServices } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { PriceDetailsLegalFinlandDialog } from '~/app/pages/summary/dialogs/price-detail/core/price-detail-legal-fi.dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { BasicProps } from '~/models/basic-props';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function CalculationInfoLegalFinland({ className }: BasicProps) {
	const { config, formatter } = useMarketConfig();
	const { loadingFlags, catalog, carIdentifier } = useConfigurator();
	const showLoadingIndicator = loadingFlags.priceSummary;
	const currentView = useAtomValue(state.currentView);
	const { i18n } = useI18n();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const totalPrice = catalog.priceSummary?.totalPrice;

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	const {
		featureServices: { footnoteService },
	} = useAppServices();
	footnoteService?.getData();

	const { openDialog, isDialogOpen, closeDialog } = useDialogs();

	const trackClickEvent = useCallback((eventName: string, label: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (price bar) - click to open ${eventName}`,
			componentName: 'configurator-price-bar',
			label,
		});
	}, []);

	return (
		<CalculationInfoStyle $view={currentView} className={className}>
			{showLoadingIndicator ? (
				<Text
					tag={'p'}
					variant={{ type: 'copy', order: '1', style: 'bold' }}
					className="calculation-info-item loading">
					{i18n('ngc.currency.calculatingPrice')}...
				</Text>
			) : (
				<>
					<div data-testid={DATA_TEST_ID.TOTAL_PRICE_CONTAINER} className={'calculation-info total-price'}>
						<PriceBarTotalPrice>
							<Text
								tag={'p'}
								variant={{ type: 'copy', order: '2', style: 'normal' }}
								className="calculation-info-item">
								{i18n('ngc.priceDetails.totalPrice')}
							</Text>
							<Layout>
								<TotalPrice>{formatter.formatPrice(totalPrice?.value)}</TotalPrice>
								<Text
									tag={'p'}
									variant={{ type: 'copy', order: '1', style: 'bold' }}
									className="calculation-info-item">
									{config.enablePriceDisclaimer && <PriceFootnote text={priceDisclaimerText} />}
								</Text>
							</Layout>
						</PriceBarTotalPrice>

						<TextLink
							className="text-grey"
							onClick={() => {
								openDialog('priceDetails');
								trackClickEvent(LayerType.PriceDetailsLayer, i18n('ngc.actions.showPriceDetails'));
							}}
							size={'sm'}
							variant={'standalone'}>
							{i18n('ngc.actions.showPriceDetails')}
						</TextLink>

						<PriceDetailsLegalFinlandDialog
							open={isDialogOpen('priceDetails')}
							onClose={() => {
								appTrackingService.summaryClickEvent({
									eventAction: EventAction.InternalLink,
									eventName: `configurator (${LayerType.PriceDetailsLayer}) - click to close layer`,
									componentName: 'price-details-layer',
								});
								appTrackingService.updateLayerComponent(LayerType.PriceDetailsLayer, 'close');
								appTrackingService.viewChangeEvent({
									eventName: `generic`,
									componentName: 'price-details-layer',
								});
								closeDialog('priceDetails');
							}}
						/>
					</div>
				</>
			)}
		</CalculationInfoStyle>
	);
}
